import { useState } from "react"
import { useQuery, useMutation } from "@tanstack/react-query"
import { authorizedFetch } from "@/lib/api"
import { SiteHeader } from "@/components/site-header"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table"
import { Pagination } from "@/components/ui/pagination"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { DownloadIcon, FileIcon } from "lucide-react"

// ─── Types ────────────────────────────────────────────────────────────────────

interface ImportLogRow {
  id: string
  userId: string
  userName: string
  fileName: string
  s3Key: string
  fileSizeBytes: number | null
  formatVersion: string
  imported: number
  updated: number
  unchanged: number
  createdAt: string
}

interface ImportLogsResponse {
  data: ImportLogRow[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25
const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001"

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatFileSize(bytes: number | null): string {
  if (bytes == null) return "—"
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${Math.round(bytes / 1024)} KB`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("es-AR")
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function CrtImportLogsPage() {
  const [page, setPage] = useState(1)

  const { data, isLoading } = useQuery<ImportLogsResponse>({
    queryKey: ["crt-import-logs", page],
    queryFn: async () => {
      const res = await authorizedFetch(
        `${API_URL}/api/smart-gt/import-logs?page=${page}&pageSize=${PAGE_SIZE}`,
      )
      if (!res.ok) throw new Error("Error al cargar el historial de importaciones")
      return res.json() as Promise<ImportLogsResponse>
    },
  })

  const downloadMut = useMutation({
    mutationFn: async (id: string) => {
      const res = await authorizedFetch(
        `${API_URL}/api/smart-gt/import-logs/${id}/download`,
      )
      if (!res.ok) throw new Error("Error al obtener el enlace de descarga")
      const json = (await res.json()) as { url: string }
      return json.url
    },
    onSuccess: (url) => {
      window.open(url, "_blank")
    },
  })

  const rows       = data?.data ?? []
  const total      = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1

  return (
    <>
      <SiteHeader title="Log de importación CRT" />

      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Historial de archivos importados</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="rounded-b-lg border-t overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Archivo</TableHead>
                    <TableHead className="w-24">Formato</TableHead>
                    <TableHead className="w-28 text-right">Importados</TableHead>
                    <TableHead className="w-28 text-right">Actualizados</TableHead>
                    <TableHead className="w-28 text-right">Sin cambios</TableHead>
                    <TableHead className="w-24 text-right">Tamaño</TableHead>
                    <TableHead className="w-40">Fecha</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-16 text-muted-foreground">
                        Cargando…
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-16 text-muted-foreground">
                        No hay importaciones registradas aún.
                      </TableCell>
                    </TableRow>
                  ) : rows.map((row) => (
                    <TableRow key={row.id}>
                      {/* Usuario */}
                      <TableCell className="text-sm font-medium">
                        {row.userName}
                      </TableCell>

                      {/* Archivo */}
                      <TableCell className="text-sm">
                        <span className="flex items-center gap-1.5 min-w-0">
                          <FileIcon className="size-3.5 shrink-0 text-muted-foreground" />
                          <span className="line-clamp-2 break-all font-mono text-xs">
                            {row.fileName}
                          </span>
                        </span>
                      </TableCell>

                      {/* Formato */}
                      <TableCell>
                        {row.formatVersion === "v2" ? (
                          <Badge className="text-[10px] px-1.5 py-0 bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400">
                            v2
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">
                            {row.formatVersion}
                          </Badge>
                        )}
                      </TableCell>

                      {/* Importados */}
                      <TableCell className="text-right tabular-nums">
                        {row.imported > 0 ? (
                          <span className="text-emerald-600 dark:text-emerald-400 font-medium text-sm">
                            {row.imported}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-sm">0</span>
                        )}
                      </TableCell>

                      {/* Actualizados */}
                      <TableCell className="text-right tabular-nums">
                        {row.updated > 0 ? (
                          <span className="text-blue-600 dark:text-blue-400 font-medium text-sm">
                            {row.updated}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-sm">0</span>
                        )}
                      </TableCell>

                      {/* Sin cambios */}
                      <TableCell className="text-right tabular-nums">
                        <span className="text-muted-foreground text-sm">
                          {row.unchanged}
                        </span>
                      </TableCell>

                      {/* Tamaño */}
                      <TableCell className="text-right text-xs text-muted-foreground tabular-nums">
                        {formatFileSize(row.fileSizeBytes)}
                      </TableCell>

                      {/* Fecha */}
                      <TableCell className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                        {formatDate(row.createdAt)}
                      </TableCell>

                      {/* Descargar */}
                      <TableCell className="text-right pr-3">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          title="Descargar archivo"
                          disabled={downloadMut.isPending}
                          onClick={() => downloadMut.mutate(row.id)}
                        >
                          <DownloadIcon className="size-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={total}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
